import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import React, { useState } from "react";

import type { IssueRow } from "#app/lib/data";
import { AddressValueCell } from "#app/components/fields/address-value-cell";
import { CardActionTooltip } from "./issue-card-action-tooltip";
import {
  CardTokenShell,
  type FieldLabelMode,
  type FieldLayout,
  type TextLabelLayout,
} from "./issue-card-fields";

/**
 * The card's valued Address row: the shared {@link AddressValueCell} in the
 * card token shell, so the property edits IN PLACE on the board exactly as it
 * does on the project drawer and in the table — the Places search input
 * mounts where the text rests, suggestions float below — instead of a
 * popover of its own. The token is the cell's chrome (brand edge while the
 * search is live), and it holds the drag sensor off while live, the same
 * way the custom-field tokens do.
 */
export function IssueCardAddress({
  issue,
  canApply,
  onSelect,
  onClear,
  labelMode,
  labelLayout,
  layout,
  labelRail,
}: {
  issue: IssueRow;
  canApply: boolean;
  onSelect: (wholeAddress: string) => void;
  onClear: () => void;
  labelMode: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout: FieldLayout;
  labelRail?: number;
}) {
  const [live, setLive] = useState(false);
  const token = (
    <CardTokenShell
      label="Address"
      icon={PROPERTY_ICON.address}
      labelMode={labelMode}
      labelLayout={labelLayout}
      layout={layout}
      labelRail={labelRail}
      // Hugs the address like every token (a long one truncates at the row's
      // width); the shell spans the row on its own while the search is live.
      pressable={canApply}
      // Own the press (the cell's trigger and copy action live inside) so it
      // never bubbles to the card press that opens the issue.
      onClick={(e) => e.stopPropagation()}
      // While the search is live, a pointer-down inside (typing, pressing a
      // suggestion in the floating list — React bubbles portal events up this
      // tree) must not arm the board's drag sensor.
      onPointerDown={live ? (e) => e.stopPropagation() : undefined}
      tone={live ? "editing" : undefined}
      valueInset={false}
    >
      <AddressValueCell
        wholeAddress={issue.address?.wholeAddress}
        addressId={issue.addressId}
        inset="card"
        // The token clips its own corners.
        bleedEnd={false}
        readOnly={!canApply}
        onSelect={onSelect}
        onClear={onClear}
        onActiveChange={setLive}
      />
    </CardTokenShell>
  );
  // Text label mode spells "Address" in the label segment; icon mode keeps
  // the name one hover away, standing down while the search is live.
  if (labelMode === "text") return token;
  return (
    <CardActionTooltip label="Address" fill={live} disabled={live}>
      {token}
    </CardActionTooltip>
  );
}
